import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/layouts/PublicLayout';
import barterService from '../../services/barter.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import Button from '../../components/common/Button';
import { RefreshIcon } from '../../components/icons/Icons';
import { formatCurrency } from '../../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import BarterExchangeRequestModal from '../../components/barter/BarterExchangeRequestModal';

const cardStyle = {
  background: theme.colors.white,
  borderRadius: theme.borderRadius.xl,
  overflow: 'hidden',
  boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
  border: `1px solid ${theme.colors.gray[100]}`,
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
};

const indigo = {
  50:  '#eef2ff',
  100: '#e0e7ff',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
};

const BarterPage = () => {
  const [barters, setBarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBarter, setSelectedBarter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestedBarters, setRequestedBarters] = useState({}); // Track which barters have requests
  const toast = useToast();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    fetchBarters();
  }, []);

  useEffect(() => {
    if (isAuthenticated && barters.length > 0) {
      checkExistingRequests();
    }
  }, [isAuthenticated, barters]);

  const fetchBarters = async () => {
    setLoading(true);
    try {
      const res = await barterService.list();
      setBarters(res.data?.barters || res.barters || []);
    } catch (err) {
      toast.error('Failed to load barter listings.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingRequests = async () => {
    const requests = {};
    for (const barter of barters) {
      try {
        const res = await barterService.checkExistingRequest(barter.id);
        if (res.data.hasRequest) {
          requests[barter.id] = true;
        }
      } catch (err) {
        // Ignore errors for individual checks
      }
    }
    setRequestedBarters(requests);
  };

  const handleExchangeRequest = async (barter) => {
    if (!isAuthenticated) {
      toast.error('Please login to request an exchange.');
      navigate('/login');
      return;
    }

    // Check if user has already submitted a request
    try {
      const res = await barterService.checkExistingRequest(barter.id);
      if (res.data.hasRequest) {
        toast.error('You have already submitted an exchange request for this product.');
        return;
      }
    } catch (err) {
      console.error('Error checking existing request:', err);
    }

    setSelectedBarter(barter);
    setModalOpen(true);
  };

  const handleSubmitRequest = async (formData, images) => {
    setSubmitting(true);
    try {
      console.log('Submitting exchange request:', { formData, imagesCount: images.length });
      await barterService.submitExchangeRequest(selectedBarter.id, formData, images);
      toast.success('Exchange request submitted successfully!');
      setModalOpen(false);
      setSelectedBarter(null);
      // Mark this barter as requested
      setRequestedBarters(prev => ({ ...prev, [selectedBarter.id]: true }));
    } catch (err) {
      console.error('Exchange request error:', err.response?.data || err);
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div style={{ backgroundColor: theme.colors.gray[50], minHeight: '100vh', padding: `${theme.spacing['3xl']} 0` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: `0 ${theme.spacing.xl}` }}>
          
          {/* Header Section */}
          <div style={{
            textAlign: 'center',
            marginBottom: theme.spacing['3xl'],
            marginTop: '34px',
          }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
                boxShadow: `0 10px 25px ${indigo[400]}80`,
                marginBottom: theme.spacing.lg
              }}
            >
              <RefreshIcon size={32} color={theme.colors.white} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.sm,
                letterSpacing: '-0.5px'
              }}
            >
              Barter Exchange
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                fontSize: theme.typography.fontSize.lg,
                color: theme.colors.text.secondary,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6
              }}
            >
              Trade your items with others. Find what you need by offering what you have.
            </motion.p>
          </div>

          {/* Grid Section */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.spacing.xl }}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} style={{ ...cardStyle, animation: 'pulse 1.5s infinite', backgroundColor: theme.colors.gray[100] }}>
                  <div style={{ height: '240px', backgroundColor: theme.colors.gray[200] }} />
                  <div style={{ padding: theme.spacing.lg }}>
                    <div style={{ height: '20px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '12px' }} />
                    <div style={{ height: '14px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '8px', width: '80%' }} />
                    <div style={{ height: '14px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '24px', width: '60%' }} />
                    <div style={{ height: '40px', backgroundColor: theme.colors.gray[200], borderRadius: theme.borderRadius.md }} />
                  </div>
                </div>
              ))}
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
            </div>
          ) : barters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', backgroundColor: theme.colors.white, borderRadius: theme.borderRadius['2xl'], border: `1px dashed ${theme.colors.gray[300]}` }}>
              <RefreshIcon size={48} color={theme.colors.gray[400]} />
              <h3 style={{ marginTop: theme.spacing.lg, fontSize: theme.typography.fontSize.xl, color: theme.colors.text.primary }}>No Barter Items Available</h3>
              <p style={{ color: theme.colors.text.secondary, marginTop: theme.spacing.sm }}>There are currently no active items listed for exchange.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.spacing.xl }}>
              <AnimatePresence>
                {barters.map((barter, index) => (
                  <motion.div
                    key={barter.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                    style={cardStyle}
                  >
                    {/* Image Area */}
                    <div style={{ height: '240px', position: 'relative', overflow: 'hidden', backgroundColor: theme.colors.gray[100] }}>
                      {barter.images && barter.images.length > 0 ? (
                        <img
                          src={barter.images[0]}
                          alt={barter.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <RefreshIcon size={48} color={theme.colors.gray[300]} />
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        top: theme.spacing.sm,
                        right: theme.spacing.sm,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        padding: '4px 10px',
                        borderRadius: theme.borderRadius.full,
                        fontSize: theme.typography.fontSize.xs,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: indigo[600],
                        boxShadow: theme.shadows.sm,
                        textTransform: 'capitalize'
                      }}>
                        {barter.condition}
                      </div>
                    </div>

                    {/* Content Area */}
                    <div style={{ padding: theme.spacing.xl, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <h3 style={{
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.text.primary,
                        marginBottom: theme.spacing.xs,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {barter.title}
                      </h3>
                      
                      <div style={{ 
                        fontSize: theme.typography.fontSize.sm, 
                        fontWeight: theme.typography.fontWeight.medium,
                        color: indigo[600],
                        marginBottom: theme.spacing.sm
                      }}>
                        Wants: {barter.exchange_with}
                      </div>

                      <p style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                        marginBottom: theme.spacing.lg,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                      }}>
                        {barter.description || 'No description available for this item.'}
                      </p>

                      <div style={{ marginTop: 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.lg, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.gray[100]}` }}>
                          <div>
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, display: 'block' }}>Estimated Value</span>
                            <span style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>
                              {formatCurrency(barter.price)}
                            </span>
                          </div>
                          {barter.seller?.store_name && (
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, display: 'block' }}>Listed by</span>
                              <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                                {barter.seller.store_name}
                              </span>
                            </div>
                          )}
                        </div>

                        <Button
                          variant={requestedBarters[barter.id] ? "outline" : "primary"}
                          fullWidth
                          onClick={() => handleExchangeRequest(barter)}
                          disabled={requestedBarters[barter.id]}
                          style={requestedBarters[barter.id] ? {
                            padding: '12px',
                            fontWeight: theme.typography.fontWeight.bold,
                            borderColor: theme.colors.success,
                            color: theme.colors.success,
                            cursor: 'not-allowed',
                            opacity: 0.7,
                          } : {
                            padding: '12px',
                            fontWeight: theme.typography.fontWeight.bold,
                            background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
                            border: 'none',
                            boxShadow: `0 4px 15px ${indigo[400]}60`,
                            transition: 'all 0.2s',
                          }}
                        >
                          {requestedBarters[barter.id] ? '✓ Request Sent' : 'Request Exchange'}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      <BarterExchangeRequestModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedBarter(null);
        }}
        onSubmit={handleSubmitRequest}
        loading={submitting}
      />
    </PublicLayout>
  );
};

export default BarterPage;
